import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import type { RootState } from '../../app/store';
import { CropType } from '../../services/entities/impl/blfApi/schemas/variety';
import { FeaturesById } from '../map/DrawControl';

export enum MapDrawIDEnum {
  blockBoundary = 'blockBoundary',
  xNumberOrientation = 'xNumberOrientation',
  yRowOrientation = 'yRowOrientation',
}

export enum OnboardingTabsEnum {
  customers = 'Customers',
  ranches = 'Ranches',
  blocks = 'Blocks',
  users = 'Users',
}

export enum OnboardFormStepEnum {
  View = 'View',
  Add = 'Add',
  Edit = 'Edit',
  Delete = 'Delete',
}

export type UploadedBlockTypes = FeatureCollection<
  Geometry,
  GeoJsonProperties
> | null;

export enum MapOnboardLayerEnum {
  blockOutlines = 'blockOutlines',
  blockOrientations = 'blockOrientations',
  surroundingBlocks = 'surroundingBlocks',
  uploadedFileOutline = 'uploadedFileOutline',
}

interface OnboardingState {
  isOpenBlockBoundaryDialog: boolean;
  currMapDrawID: MapDrawIDEnum;
  currMapDrawFeatures: FeaturesById;
  currOnboardingTab: OnboardingTabsEnum;
  filterCustomerID: string;
  filterRanchID: string;
  filterCropType: CropType | null;
  invalidItemsCheckbox: boolean;
  uploadedBlockGeoJSON: UploadedBlockTypes;
  search: string;
  onboardFormStep: OnboardFormStepEnum;
  currOnboardCustomerID: string;
  currOnboardRanchID: string;
  currOnboardBlockID: string;
  currOnboardUserID: string;
  clearMapDrawFeatures: number;
  showSurroundingBlocksOnMap: boolean;
}

const initialState: OnboardingState = {
  isOpenBlockBoundaryDialog: false,
  currMapDrawID: MapDrawIDEnum.blockBoundary,
  currMapDrawFeatures: {},
  currOnboardingTab: OnboardingTabsEnum.blocks,
  filterCustomerID: '',
  filterRanchID: '',
  filterCropType: null,
  invalidItemsCheckbox: false,
  uploadedBlockGeoJSON: null,
  search: '',
  onboardFormStep: OnboardFormStepEnum.Add,
  currOnboardCustomerID: '',
  currOnboardRanchID: '',
  currOnboardBlockID: '',
  currOnboardUserID: '',
  clearMapDrawFeatures: 10,
  showSurroundingBlocksOnMap: true,
};

export const onboardingState = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    initOnboardingState: () => initialState,
    setIsOpenBlockBoundaryDialog: (state, action: PayloadAction<boolean>) => {
      state.isOpenBlockBoundaryDialog = action.payload;
      // Reset the map draw features and uploaded geoJSON when the dialog is closed
      if (!action.payload) {
        state.currMapDrawFeatures = {};
        state.uploadedBlockGeoJSON = null;
      }
    },
    setCurrMapDrawID: (state, action: PayloadAction<MapDrawIDEnum>) => {
      state.currMapDrawID = action.payload;
    },
    setCurrMapDrawFeatures: (state, action: PayloadAction<FeaturesById>) => {
      state.currMapDrawFeatures = action.payload;
    },
    setCurrOnboardingTab: (
      state,
      action: PayloadAction<OnboardingTabsEnum>
    ) => {
      state.currOnboardingTab = action.payload;
    },
    setFilterCustomerID: (state, action: PayloadAction<string>) => {
      state.filterCustomerID = action.payload;
    },
    setFilterRanchID: (state, action: PayloadAction<string>) => {
      state.filterRanchID = action.payload;
    },
    setFilterCropType: (state, action: PayloadAction<CropType | null>) => {
      state.filterCropType = action.payload;
    },
    setInvalidItemsCheckbox: (state, action: PayloadAction<boolean>) => {
      state.invalidItemsCheckbox = action.payload;
    },
    setUploadedBlockGeoJSON: (
      state,
      action: PayloadAction<UploadedBlockTypes>
    ) => {
      state.uploadedBlockGeoJSON = action.payload;
    },
    setOnboardSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setOnboardFormStep: (state, action: PayloadAction<OnboardFormStepEnum>) => {
      state.onboardFormStep = action.payload;
    },
    setCurrOnboardCustomerID: (state, action: PayloadAction<string>) => {
      state.currOnboardCustomerID = action.payload;
      state.currOnboardRanchID = '';
      state.currOnboardBlockID = '';
      state.currOnboardUserID = '';
    },
    setCurrOnboardRanchID: (state, action: PayloadAction<string>) => {
      state.currOnboardRanchID = action.payload;
      state.currOnboardBlockID = '';
    },
    setCurrOnboardBlockID: (state, action: PayloadAction<string>) => {
      state.currOnboardBlockID = action.payload;
    },
    setCurrOnboardUserID: (state, action: PayloadAction<string>) => {
      state.currOnboardUserID = action.payload;
    },
    clearMapDrawFeatures: (state) => {
      // This is a hacky method to clear the mapbox draw controls features by changing the DrawControl key so it reloads.
      state.clearMapDrawFeatures += 1;
    },
    toggleShowSurroundingBlocksOnMap: (state) => {
      state.showSurroundingBlocksOnMap = !state.showSurroundingBlocksOnMap;
    },
  },
});

export const {
  initOnboardingState,
  setIsOpenBlockBoundaryDialog,
  setCurrMapDrawID,
  setCurrMapDrawFeatures,
  setCurrOnboardingTab,
  setFilterCustomerID,
  setFilterRanchID,
  setFilterCropType,
  setInvalidItemsCheckbox,
  setUploadedBlockGeoJSON,
  setOnboardSearch,
  setOnboardFormStep,
  setCurrOnboardCustomerID,
  setCurrOnboardRanchID,
  setCurrOnboardBlockID,
  setCurrOnboardUserID,
  clearMapDrawFeatures,
  toggleShowSurroundingBlocksOnMap,
} = onboardingState.actions;

export const selectIsOpenBlockBoundaryDialog = (state: RootState) =>
  state.onboarding.isOpenBlockBoundaryDialog;
export const selectCurrMapDrawID = (state: RootState) =>
  state.onboarding.currMapDrawID;
export const selectCurrMapDrawFeatures = (state: RootState) =>
  state.onboarding.currMapDrawFeatures;
export const selectCurrOnboardingTab = (state: RootState) =>
  state.onboarding.currOnboardingTab;
export const selectFilterCustomerID = (state: RootState) =>
  state.onboarding.filterCustomerID;
export const selectFilterRanchID = (state: RootState) =>
  state.onboarding.filterRanchID;
export const selectFilterCropType = (state: RootState) =>
  state.onboarding.filterCropType;
export const selectInvalidItemsCheckbox = (state: RootState) =>
  state.onboarding.invalidItemsCheckbox;
export const selectUploadedBlockGeoJSON = (state: RootState) =>
  state.onboarding.uploadedBlockGeoJSON;
export const selectOnboardSearch = (state: RootState) =>
  state.onboarding.search;
export const selectOnboardFormStep = (state: RootState) =>
  state.onboarding.onboardFormStep;
export const selectCurrOnboardCustomerID = (state: RootState) =>
  state.onboarding.currOnboardCustomerID;
export const selectCurrOnboardRanchID = (state: RootState) =>
  state.onboarding.currOnboardRanchID;
export const selectCurrOnboardBlockID = (state: RootState) =>
  state.onboarding.currOnboardBlockID;
export const selectCurrOnboardUserID = (state: RootState) =>
  state.onboarding.currOnboardUserID;
export const selectClearMapDrawFeatures = (state: RootState) =>
  state.onboarding.clearMapDrawFeatures;
export const selectShowSurroundingBlocksOnMap = (state: RootState) =>
  state.onboarding.showSurroundingBlocksOnMap;
export const selectAllOnboardingState = (state: RootState) => state.onboarding;

export default onboardingState.reducer;
